import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import SectionWrapper from "../../Widgets/SectionWrapper";
import EditFields from "./EditFields";
import ViewFields from "./ViewFields";
import { CriminalHistoryFormValues } from "./interface";
import useGetDrugCriminalHistory from "hooks/api/client/useGetDrugCriminalHistory";
import useUpsertDrugCriminalHistory from "hooks/api/client/useUpsertDrugCriminalHistory";

const EMPTY_VALUES: CriminalHistoryFormValues = {
  drugAlcoholUse: "",
  criminalLegalHistory: "",
};

export const CriminalHistory: React.FC = () => {
  const { id: clientId } = useParams<{ id: string }>();
  const clientIdNum = clientId ? Number(clientId) : null;

  const { drugCriminalHistory, refetchDrugCriminalHistory } = useGetDrugCriminalHistory(clientIdNum);

  const { upsertDrugCriminalHistoryMutation, isUpsertDrugCriminalHistoryLoading } = useUpsertDrugCriminalHistory({
    onSuccess: () => {
      refetchDrugCriminalHistory();
      setEditMode(false);
    },
  });

  const [editMode, setEditMode] = useState(false);
  const [formValues, setFormValues] = useState<CriminalHistoryFormValues>(EMPTY_VALUES);
  const [initialValues, setInitialValues] = useState<CriminalHistoryFormValues>(EMPTY_VALUES);

  useEffect(() => {
    if (drugCriminalHistory) {
      setFormValues(drugCriminalHistory);
      setInitialValues(drugCriminalHistory);
    }
  }, [drugCriminalHistory]);

  const handleChange =
    (field: keyof CriminalHistoryFormValues) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setFormValues((prev) => ({ ...prev, [field]: event.target.value }));
    };

  const handleSave = () => {
    if (!clientIdNum) return;
    upsertDrugCriminalHistoryMutation({ clientId: clientIdNum, payload: formValues });
  };

  const handleCancel = () => {
    setFormValues(initialValues);
    setEditMode(false);
  };

  const saveDisabled =
    JSON.stringify(formValues) === JSON.stringify(initialValues) ||
    isUpsertDrugCriminalHistoryLoading;

  return (
    <SectionWrapper
      title="Current Drug Use & Criminal History"
      description="Substance use and legal circumstances that may affect safety planning, home environment considerations, or treatment approach."
      editMode={editMode}
      onEdit={() => setEditMode(true)}
      onCancel={handleCancel}
      onSave={handleSave}
      saveDisabled={saveDisabled}
    >
      {editMode ? (
        <EditFields formValues={formValues} handleChange={handleChange} />
      ) : (
        <ViewFields formValues={formValues} />
      )}
    </SectionWrapper>
  );
};

export default CriminalHistory;
